import React from "react";
import defaultLogo from "../../Header/Images/RMB-Strip-Jaipur.png";

/**
 * RMB Jaipur – "Welcome to the Family" New Member Poster (1:1 / 1600 x 1600 grid)
 *
 * Built on the exact 1600 x 1600 grid. SVG-native vector layout ensures 100%
 * compatibility with html2canvas and pixel-perfect responsive display.
 */

const BLUE = "#1c3f94";
const BLUE_DK = "#1a3a8a";
const GOLD = "#f0b429";

const serifFont = "'Fraunces', Georgia, serif";
const arimoFont = "'Arimo', Arial, Helvetica, sans-serif";
const poppinsFont = "'Poppins', system-ui, sans-serif";

const Star = ({ x, y, s = 34 }) => (
  <path
    transform={`translate(${x - s / 2} ${y - s / 2}) scale(${s / 24})`}
    d="M12 0C12.8 7.5 16.5 11.2 24 12 16.5 12.8 12.8 16.5 12 24 11.2 16.5 7.5 12.8 0 12 7.5 11.2 11.2 7.5 12 0Z"
    fill={GOLD}
  />
);

// [x, y, w, h, rotation]
const CONFETTI = [
  [60, 45, 46, 12, 55], [100, 70, 30, 10, -35], [175, 38, 22, 22, 20], [130, 120, 40, 10, 70],
  [30, 205, 36, 10, -25], [160, 275, 24, 24, 35], [95, 235, 30, 9, 15], [250, 110, 28, 9, -60],
  [1470, 95, 44, 12, -40], [1535, 62, 30, 10, 30], [1560, 160, 28, 9, 60], [1425, 235, 34, 10, 20],
  [1500, 330, 30, 10, -30], [1565, 285, 24, 24, 15], [1535, 435, 40, 10, 40], [1555, 575, 26, 26, 20],
  [520, 250, 30, 9, -45], [1010, 235, 28, 9, 30], [1235, 270, 34, 10, -20], [690, 265, 26, 8, 50],
  [40, 540, 34, 10, 40], [30, 440, 24, 24, 10], [1385, 395, 30, 9, -50], [235, 330, 26, 8, 25],
];

const RmbWelcomeCard = React.forwardRef((props, ref) => {
  const {
    logo = defaultLogo,
    year = "2026-27",
    title = "Welcome to RMB Jaipur Family",
    member = {
      name: ["RANVEER SINGH", "NATHAWAT"],
      company: ["Homeland Real Estate", "Pvt Ltd."],
      dealsIn: ["Deals in: Real Estate.", "Colonizer"],
      photo: "",
    },
    sponsor = {
      name: "DILIP MORANI",
      company: "Valuefundz Pvt Ltd",
      role: "Financial planner",
      photo: "",
    },
    contact = "9214001400",
    website = "https://rmbjaipur.com/",
    className = "",
    style = {},
  } = props;

  // Normalize member name into 1 or 2 lines
  let memNameLines = ["NEW MEMBER", ""];
  if (Array.isArray(member?.name)) {
    memNameLines = [member.name[0] || "", member.name[1] || ""];
  } else if (typeof member?.name === "string" && member.name.trim()) {
    const parts = member.name.trim().split(" ");
    if (parts.length > 2) {
      memNameLines = [parts.slice(0, 2).join(" ").toUpperCase(), parts.slice(2).join(" ").toUpperCase()];
    } else if (parts.length === 2) {
      memNameLines = [parts[0].toUpperCase(), parts[1].toUpperCase()];
    } else {
      memNameLines = [member.name.toUpperCase(), ""];
    }
  }

  // Normalize member company into 1 or 2 lines
  let compLines = ["Company Name", ""];
  if (Array.isArray(member?.company)) {
    compLines = [member.company[0] || "", member.company[1] || ""];
  } else if (typeof member?.company === "string" && member.company.trim()) {
    const c = member.company.trim();
    if (c.length > 24) {
      const mid = c.lastIndexOf(" ", 24);
      const splitAt = mid > 0 ? mid : 24;
      compLines = [c.substring(0, splitAt), c.substring(splitAt).trim()];
    } else {
      compLines = [c, ""];
    }
  }

  // Normalize dealsIn into 1 or 2 lines
  let dealsLines = ["Deals in: Business Services", ""];
  if (Array.isArray(member?.dealsIn)) {
    dealsLines = [member.dealsIn[0] || "", member.dealsIn[1] || ""];
  } else if (typeof member?.dealsIn === "string" && member.dealsIn.trim()) {
    let d = member.dealsIn.trim();
    if (!d.toLowerCase().startsWith("deals in")) {
      d = `Deals in: ${d}`;
    }
    if (d.length > 32) {
      const mid = d.lastIndexOf(" ", 32);
      const splitAt = mid > 0 ? mid : 32;
      dealsLines = [d.substring(0, splitAt), d.substring(splitAt).trim()];
    } else {
      dealsLines = [d, ""];
    }
  }

  const memberPhoto = member?.photo || "";
  const sponsorPhoto = sponsor?.photo || "";
  const sponsorName = sponsor?.name || "RMB Jaipur Leader";
  const sponsorCompany = sponsor?.company || "RMB Jaipur Fellowship";
  const sponsorRole = sponsor?.role || "Member";

  return (
    <div
      ref={ref}
      className={`rmb-welcome-card relative overflow-hidden bg-white shadow-2xl rounded-3xl select-none ${className}`}
      style={{
        width: "100%",
        maxWidth: "600px",
        aspectRatio: "1 / 1",
        margin: "0 auto",
        ...style,
      }}
    >
      <svg
        viewBox="0 0 1600 1600"
        className="w-full h-full block"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        <defs>
          <linearGradient id="bgGrey" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#c9cad0" />
            <stop offset="1" stopColor="#f1f1f4" />
          </linearGradient>
          <linearGradient id="bgTop" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#e9ebf2" />
            <stop offset="0.5" stopColor="#ffffff" />
            <stop offset="1" stopColor="#eceef4" />
          </linearGradient>
          <linearGradient id="goldGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#f6dc8c" />
            <stop offset="1" stopColor="#d4a238" />
          </linearGradient>
          <linearGradient id="spGrad2" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#223f8c" />
            <stop offset="1" stopColor="#4562b3" />
          </linearGradient>

          {/* Member Photo Clip Circle */}
          <clipPath id="memberPhotoClip">
            <circle cx="370" cy="690" r="295" />
          </clipPath>
          {/* Sponsor Photo Clip Circle */}
          <clipPath id="sponsorPhotoClip">
            <circle cx="1081" cy="1345" r="164" />
          </clipPath>
        </defs>

        {/* ─────────── LAYER 1: Background geometric shapes ─────────── */}
        <rect width="1600" height="1600" fill="url(#bgTop)" />
        <polygon points="0,930 560,1110 0,1517" fill="url(#bgGrey)" opacity="0.75" />
        <polygon points="0,1517 1600,1517 1600,1130 900,1050 330,1180" fill="#f3f3f6" opacity="0.8" />
        <polygon points="1000,1060 1600,1160 1600,1250" fill="#e6e7ec" opacity="0.7" />

        {/* Confetti particles */}
        {CONFETTI.map(([x, y, w, h, r], i) => (
          <rect
            key={i}
            x={x}
            y={y}
            width={w}
            height={h}
            rx="2"
            fill="url(#goldGrad)"
            opacity="0.8"
            transform={`rotate(${r} ${x + w / 2} ${y + h / 2})`}
          />
        ))}
        <path d="M60 60 q30 -30 55 0 t55 0" stroke="#d9b45a" strokeWidth="9" fill="none" opacity="0.6" strokeLinecap="round" />
        <path d="M1440 120 q30 -40 55 -5 t50 -10" stroke="#d9b45a" strokeWidth="9" fill="none" opacity="0.6" strokeLinecap="round" />
        <path d="M20 560 q40 -30 60 10 t70 0" stroke="#d9b45a" strokeWidth="9" fill="none" opacity="0.5" strokeLinecap="round" />

        {/* Top Dark Blue Arch Banner */}
        <polygon
          points="236,30 1364,30 1398,52 1160,160 440,160 202,52"
          fill={BLUE}
          stroke={BLUE}
          strokeWidth="60"
          strokeLinejoin="round"
        />

        {/* Top White Logo Plaque */}
        <rect x="443" y="35" width="714" height="118" rx="8" fill="#ffffff" />
        {logo && (
          <image
            href={logo}
            x="460"
            y="42"
            width="680"
            height="104"
            preserveAspectRatio="xMidYMid meet"
          />
        )}

        {/* Year Badge */}
        <text
          x="800"
          y="215"
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={serifFont}
          fontSize="44"
          fontWeight="900"
          fontStyle="italic"
          fill="#e0a22c"
        >
          {year}
        </text>

        {/* Welcome Big Header */}
        <text
          x="800"
          y="325"
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={serifFont}
          fontSize="92"
          fontWeight="900"
          fontStyle="italic"
          fill={BLUE}
        >
          {title}
        </text>

        {/* Gold outline & Company Panel */}
        <path
          d="M600 940 L1385 944 Q1485 940 1498 860 L1522 610 Q1528 548 1478 525"
          fill="none"
          stroke={GOLD}
          strokeWidth="6"
          strokeLinecap="round"
        />
        <polygon
          points="600,520 1390,498 1462,552 1412,852 1362,876 660,876"
          fill={BLUE}
          stroke={BLUE}
          strokeWidth="62"
          strokeLinejoin="round"
        />

        {/* Company Panel Texts */}
        <text
          x="1050"
          y={compLines[1] ? "580" : "628"}
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={serifFont}
          fontSize="70"
          fontWeight="900"
          fontStyle="italic"
          fill="#ffffff"
        >
          {compLines[0]}
        </text>
        {compLines[1] && (
          <text
            x="1050"
            y="676"
            dominantBaseline="central"
            textAnchor="middle"
            fontFamily={serifFont}
            fontSize="70"
            fontWeight="900"
            fontStyle="italic"
            fill="#ffffff"
          >
            {compLines[1]}
          </text>
        )}

        {/* Deals In Texts */}
        <text
          x="1050"
          y={dealsLines[1] ? "783" : "818"}
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={arimoFont}
          fontSize="46"
          fontWeight="bold"
          fill="#ffffff"
        >
          {dealsLines[0]}
        </text>
        {dealsLines[1] && (
          <text
            x="1050"
            y="853"
            dominantBaseline="central"
            textAnchor="middle"
            fontFamily={arimoFont}
            fontSize="46"
            fontWeight="bold"
            fill="#ffffff"
          >
            {dealsLines[1]}
          </text>
        )}

        {/* Rings around Member Photo */}
        <path d="M372 385 A305 305 0 0 1 676 650" fill="none" stroke={BLUE} strokeWidth="6" strokeDasharray="22 16" />
        <path d="M662 690 A292 292 0 0 1 672 780" fill="none" stroke={BLUE} strokeWidth="6" strokeDasharray="22 16" />
        <path d="M50 700 A325 325 0 0 0 330 1010" fill="none" stroke={BLUE} strokeWidth="6" strokeLinecap="round" />

        {/* ─────────── Member Photo Circle ─────────── */}
        <circle cx="370" cy="690" r="295" fill="#cbd5e1" stroke={GOLD} strokeWidth="10" />
        {memberPhoto ? (
          <image
            href={memberPhoto}
            x="75"
            y="395"
            width="590"
            height="590"
            preserveAspectRatio="xMidYMid slice"
            clipPath="url(#memberPhotoClip)"
          />
        ) : (
          <g transform="translate(230, 550) scale(11.5)">
            <path
              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
              fill="#64748b"
            />
          </g>
        )}
        <circle cx="370" cy="690" r="295" fill="none" stroke={GOLD} strokeWidth="10" />

        {/* ─────────── Member Name Pill ─────────── */}
        <rect x="62" y="962" width="625" height="128" rx="42" fill="#0a1020" />
        <rect x="55" y="952" width="625" height="128" rx="42" fill={BLUE} />
        <Star x={66} y={966} />
        <Star x={668} y={1073} />

        {/* Member Name Inside Pill */}
        <text
          x="367.5"
          y={memNameLines[1] ? "990" : "1016"}
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={arimoFont}
          fontSize="52"
          fontWeight="bold"
          fill="#ffffff"
          letterSpacing="0.02em"
        >
          {memNameLines[0]}
        </text>
        {memNameLines[1] && (
          <text
            x="367.5"
            y="1043"
            dominantBaseline="central"
            textAnchor="middle"
            fontFamily={arimoFont}
            fontSize="52"
            fontWeight="bold"
            fill="#ffffff"
            letterSpacing="0.02em"
          >
            {memNameLines[1]}
          </text>
        )}

        {/* ─────────── Sponsor Panel ─────────── */}
        <text
          x="632"
          y="1205"
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={poppinsFont}
          fontSize="72"
          fontWeight="bold"
          fontStyle="italic"
          fill="#2f4ea5"
        >
          Sponsored By
        </text>

        <rect x="282" y="1252" width="700" height="190" rx="38" fill="url(#spGrad2)" />

        <text
          x="632"
          y="1297"
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={arimoFont}
          fontSize="44"
          fontWeight="bold"
          fill={GOLD}
        >
          {sponsorName}
        </text>
        <text
          x="632"
          y="1352"
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={arimoFont}
          fontSize="30"
          fontWeight="bold"
          fill="#ffffff"
        >
          {sponsorCompany}
        </text>
        <text
          x="632"
          y="1405"
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={arimoFont}
          fontSize="24"
          fontWeight="normal"
          fill="#ffffff"
        >
          {sponsorRole}
        </text>

        {/* Sponsor Photo Circle */}
        <circle cx="1081" cy="1345" r="164" fill="#cbd5e1" stroke="#e3e5ea" strokeWidth="12" />
        {sponsorPhoto ? (
          <image
            href={sponsorPhoto}
            x="917"
            y="1181"
            width="328"
            height="328"
            preserveAspectRatio="xMidYMid slice"
            clipPath="url(#sponsorPhotoClip)"
          />
        ) : (
          <g transform="translate(1001, 1265) scale(6.5)">
            <path
              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
              fill="#64748b"
            />
          </g>
        )}
        <circle cx="1081" cy="1345" r="164" fill="none" stroke="#e3e5ea" strokeWidth="12" />

        {/* Modern decorative hexagons in bottom corner */}
        <polygon points="1417,1300 1500,1300 1535,1358 1500,1415 1417,1415 1383,1358" fill={BLUE} />
        <polygon points="1523,1290 1590,1290 1625,1345 1590,1405 1523,1405 1490,1345" fill="none" stroke="#c9cfe3" strokeWidth="3" />
        <polygon points="1555,1402 1600,1402 1600,1470 1555,1470 1522,1436" fill="#2d63a8" />
        <polygon points="1425,1418 1480,1418 1510,1468 1480,1520 1425,1520 1392,1468" fill="none" stroke="#c9cfe3" strokeWidth="3" />
        <polygon points="1448,1462 1508,1462 1530,1500 1508,1520 1448,1520 1425,1490" fill={BLUE} />

        {/* ─────────── Footer Bar ─────────── */}
        <rect x="0" y="1517" width="1600" height="83" fill={BLUE_DK} />
        <line x1="470" y1="1517" x2="430" y2="1600" stroke="#fff" strokeWidth="3" opacity="0.85" />
        <line x1="1078" y1="1517" x2="1038" y2="1600" stroke="#fff" strokeWidth="3" opacity="0.85" />

        <text
          x="235"
          y="1558"
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={arimoFont}
          fontSize="36"
          fontWeight="bold"
          fill="#ffffff"
        >
          For Membership
        </text>
        <text
          x="754"
          y="1558"
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={arimoFont}
          fontSize="36"
          fontWeight="bold"
          fill="#ffffff"
        >
          Contact: {contact}
        </text>
        <text
          x="1339"
          y="1558"
          dominantBaseline="central"
          textAnchor="middle"
          fontFamily={arimoFont}
          fontSize="36"
          fontWeight="normal"
          fill="#ffffff"
          textDecoration="underline"
        >
          {website}
        </text>
      </svg>
    </div>
  );
});

export default RmbWelcomeCard;
